/* eslint-disable import/no-dynamic-require */
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Chart from 'src/enosikit/components/Chart';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, SOURCE_HISTORIAN, SOURCE_TRADES,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';

import DashboardControlChartControlsSkeleton from './DashboardControlChartControlsSkeleton';
import { controlOptionFunc } from './helpers/common';
import DatePickerInTimezone from '../DatePickerInTimezone';
import FileDownloadSelector from '../FileDownloadSelector';

/**
 *
 * @param {object} root0
 * @param {boolean} root0.allowUnchangedApply - When true, bypasses the date-comparison
 * guard in setTimeSpan, allowing timespanUpdateFunc to fire even if the selected dates
 * have not changed. Used by callers to enable retrying a fetch for the same date range
 * (e.g., after a cancelled request when no data is available to display).
 * @param {Function} root0.controlSetStateFunc
 * @param {object} root0.chartView
 * @param {Function} root0.chartViewSelector
 * @param {Function} root0.downloadFunc
 * @param {boolean} root0.downloadSelectorOpen
 * @param {Error | null} root0.error - Error object if an error occurred, null otherwise
 * @param {boolean} root0.isModalOpen
 * @param {object} root0.mainData
 * @param {number} root0.minStart - Unix timestamp (seconds) of the earliest date selectable in
 * the date picker. When null, no minimum date is enforced.
 * @param {Function} root0.setChartViewFunc
 * @param {SOURCE_HISTORIAN |SOURCE_TRADES} root0.source
 * @param {object} root0.timespan
 * @param {Function} root0.timespanUpdateFunc
 * @param {Function} root0.toggleFunc
 * @param {UNIT_CARBON | UNIT_CURRENCY | UNIT_ENERGY} root0.unit
 * @param {any} root0.viewSelectorOpen
 * @param {string} root0.timezone
 * @returns {React.ReactComponentElement} - DashboardControl component
 */
function DashboardControl({
  allowUnchangedApply = false, controlSetStateFunc, chartView, chartViewSelector,
  downloadFunc = null, downloadSelectorOpen, error = null, isModalOpen = false, mainData,
  minStart = null, setChartViewFunc, source, timespan, timespanUpdateFunc, timezone,
  toggleFunc, unit, viewSelectorOpen,
}) {
  const intl = useIntl();

  if ((!mainData?.property && !mainData?.portfolio) || !timespan) {
    return (
      null
    );
  }

  const [start, setStart] = useState(timespan.start);
  const [finish, setFinish] = useState(timespan.finish);
  const [myRef, setMyRef] = useState(false);
  const [apply, setApply] = useState(false);

  useEffect(() => {
    if (timespan) {
      const { start: updatedStart, finish: updatedFinish } = timespan;
      setStart(updatedStart);
      setFinish(updatedFinish);
    }
  }, [timespan]);

  /**
   * Called when the date range picker's calendar is opened.
   * @returns {void}
   */
  const openCalendar = () => {
    const { start: prevStart, finish: prevFinish } = timespan;
    setStart(prevStart);
    setFinish(prevFinish);
    setApply(false);
  };

  /**
   * Called when the date range picker's calendar is closed.
   * @returns {void}
   */
  const closeCalendar = () => {
    setApply(true);
    myRef.setOpen(false);
  };

  const setTimeSpan = () => {
    if (!apply) { return; }

    const { start: prevStart, finish: prevFinish } = timespan;

    if (start === null) { setStart(prevStart.setZone(timezone)); }
    if (finish === null) { setFinish(start.setZone(timezone)); }

    // No change, nothing to see here...
    if (!allowUnchangedApply
      && start.toSeconds() === prevStart.toSeconds()
      && (finish !== null && finish.toSeconds() === prevFinish.toSeconds())) {
      return;
    }

    timespanUpdateFunc(start, (finish === null ? start : finish));
  };

  /**
   * @param {Array<DateTime>} dates
   * @returns {void}
   */
  const updateStartAndFinish = (dates) => {
    const [newStart, newFinish] = dates;

    const noRanges = !start && !finish;
    const hasStartRange = start && !finish;
    const isRangeFilled = start && finish;

    if (noRanges || isRangeFilled) {
      setStart(newStart);
      setFinish(newFinish);
      return;
    }
    if (hasStartRange) {
      setStart(newStart);
      setFinish(newFinish);
    }
  };

  if ((!mainData?.property && !mainData?.portfolio) || !timespan) {
    return (
      null
    );
  }

  const isPortfolio = !downloadFunc;
  const dateRange = { start, finish };

  return (
    <ButtonToolbar>
      <ButtonGroup className="mb-3" style={{ zIndex: 9999 }}>
        <DatePickerInTimezone
          timezone={timezone}
          start={start}
          finish={finish}
          ref={setMyRef}
          maxNumberDays={30}
          minStart={minStart}
          onChange={updateStartAndFinish}
          onCalendarClose={setTimeSpan}
          onCalendarOpen={openCalendar}
          customCalendarClose={closeCalendar}
          isModalOpen={isModalOpen}
        />
      </ButtonGroup>

      {error ? (
        <DashboardControlChartControlsSkeleton downloadFunc={downloadFunc} />
      ) : (
        <>
          <Chart.ChartControls
            buttons={controlOptionFunc(intl, unit, source, isPortfolio)}
            onButtonClick={(opts) => { controlSetStateFunc(opts); }}
          />
          <ButtonGroup>
            {chartViewSelector(
              viewSelectorOpen,
              toggleFunc,
              chartView,
              setChartViewFunc,
              source,
            )}
            {downloadFunc && (
              <FileDownloadSelector
                downloadFunc={downloadFunc}
                mainData={mainData}
                downloadSelectorOpen={downloadSelectorOpen}
                toggleFunc={toggleFunc}
                dateRange={dateRange}
              />
            )}
          </ButtonGroup>
        </>
      )}

    </ButtonToolbar>
  );
}

DashboardControl.propTypes = {
  allowUnchangedApply: PropTypes.bool,
  controlSetStateFunc: PropTypes.func.isRequired,
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
      DATA_AGGREGATE_BY_METER]).isRequired,
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE]).isRequired,
  }).isRequired,
  chartViewSelector: PropTypes.func.isRequired,
  downloadSelectorOpen: PropTypes.bool.isRequired,
  downloadFunc: PropTypes.func,
  error: PropTypes.instanceOf(Error),
  isModalOpen: PropTypes.bool,
  mainData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  minStart: PropTypes.number,
  setChartViewFunc: PropTypes.func.isRequired,
  source: PropTypes.oneOf([SOURCE_HISTORIAN, SOURCE_TRADES]).isRequired,
  unit: PropTypes.oneOf([UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY]).isRequired,
  timespan: PropTypes.shape({
    start: PropTypes.instanceOf(DateTime),
    finish: PropTypes.instanceOf(DateTime),
  }).isRequired,
  timespanUpdateFunc: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  toggleFunc: PropTypes.func.isRequired,
  viewSelectorOpen: PropTypes.bool.isRequired,
};

export default DashboardControl;
